/**
 * État et messages du renouvellement automatique du certificat. Fonctions pures (testables) :
 * le texte affiché à l'utilisateur dépend uniquement de l'échéance, de l'heure, de la grâce serveur
 * et du dernier résultat connu.
 */

/** Même valeur que RENEW_GRACE_DAYS côté serveur (30 jours après l'échéance du certificat). */
export const RENEW_GRACE_DAYS = 30;
const DAY_SEC = 86_400;

export type RenewalOutcome =
  | 'renewed'
  | 'offline'
  | 'not_due'
  | 'refused_expired'
  | 'refused_too_old'
  | 'refused_revoked'
  | 'server_error';

export interface RenewalState {
  lastAttemptAt: string;
  outcome: RenewalOutcome;
  /** Code renvoyé par le serveur en cas de refus. */
  code?: string;
}

export function parseRenewalState(raw: string | null): RenewalState | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<RenewalState>;
    const outcomes: RenewalOutcome[] = [
      'renewed',
      'offline',
      'not_due',
      'refused_expired',
      'refused_too_old',
      'refused_revoked',
      'server_error',
    ];
    if (typeof v.lastAttemptAt === 'string' && v.outcome && outcomes.includes(v.outcome)) {
      return {
        lastAttemptAt: v.lastAttemptAt,
        outcome: v.outcome,
        ...(typeof v.code === 'string' ? { code: v.code } : {}),
      };
    }
  } catch {
    /* état illisible : ignoré */
  }
  return null;
}

export function outcomeFromServerCode(code: string | undefined): RenewalOutcome {
  if (code === 'license_expired') return 'refused_expired';
  if (code === 'certificate_expired_too_long') return 'refused_too_old';
  if (code === 'license_revoked' || code === 'license_suspended' || code === 'activation_revoked') {
    return 'refused_revoked';
  }
  return 'server_error';
}

function fmt(sec: number): string {
  return new Date(sec * 1000).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function daysBetween(fromSec: number, toSec: number): number {
  return Math.ceil((toSec - fromSec) / DAY_SEC);
}

export interface RenewalDescription {
  /** Phrase principale affichée dans l'écran Licence. */
  text: string;
  /** info = tout va bien ; attention = action de l'utilisateur utile ; blocage = lecture seule. */
  level: 'info' | 'attention' | 'blocage';
}

/**
 * Message clair sur l'état du renouvellement.
 * @param expSec échéance du certificat (secondes)
 * @param windowDays fenêtre de renouvellement (jours avant l'échéance)
 */
export function describeRenewal(input: {
  expSec: number;
  windowDays: number;
  nowSec: number;
  online: boolean;
  state: RenewalState | null;
  graceDays?: number;
}): RenewalDescription {
  const grace = input.graceDays ?? RENEW_GRACE_DAYS;
  const { expSec, nowSec } = input;
  const daysLeft = daysBetween(nowSec, expSec);
  const lastOutcome = input.state?.outcome;

  if (lastOutcome === 'refused_revoked') {
    return {
      level: 'blocage',
      text: 'Renouvellement refusé : la licence a été suspendue ou révoquée. Contactez Nexxus.',
    };
  }

  if (expSec <= nowSec) {
    const graceEnd = expSec + grace * DAY_SEC;
    if (nowSec > graceEnd || lastOutcome === 'refused_too_old') {
      return {
        level: 'blocage',
        text: `Licence échue depuis plus de ${String(grace)} jours : le renouvellement automatique n’est plus possible. Réactivez ce poste avec votre clé commerciale ou importez un fichier de licence.`,
      };
    }
    if (lastOutcome === 'refused_expired') {
      return {
        level: 'blocage',
        text: 'Licence échue : votre abonnement n’est pas encore renouvelé (paiement non validé par Nexxus). Dès la validation, ce poste se met à jour tout seul à la prochaine connexion.',
      };
    }
    const until = fmt(graceEnd);
    return {
      level: 'blocage',
      text: input.online
        ? `Licence échue le ${fmt(expSec)} : lecture seule. Le renouvellement est tenté automatiquement dès que Nexxus a enregistré votre paiement (possible jusqu’au ${until}).`
        : `Licence échue le ${fmt(expSec)} : lecture seule. Connectez ce poste à Internet avant le ${until} pour qu’il se renouvelle seul, ou importez un fichier de licence.`,
    };
  }

  const windowStart = expSec - input.windowDays * DAY_SEC;
  if (nowSec < windowStart) {
    return {
      level: 'info',
      text: `Renouvellement automatique actif : licence valable jusqu’au ${fmt(expSec)} (${String(daysLeft)} jour(s)). Le poste se renouvellera seul à partir du ${fmt(windowStart)}, s’il est connecté à Internet.`,
    };
  }

  if (lastOutcome === 'refused_expired' || lastOutcome === 'server_error') {
    return {
      level: 'attention',
      text:
        lastOutcome === 'refused_expired'
          ? `Licence valable jusqu’au ${fmt(expSec)} (${String(daysLeft)} jour(s)). Votre abonnement n’est pas encore renouvelé : dès que Nexxus a validé votre paiement, ce poste se met à jour tout seul.`
          : `Licence valable jusqu’au ${fmt(expSec)} (${String(daysLeft)} jour(s)). Le serveur de licence n’a pas pu renouveler le certificat ; nouvel essai automatique dans quelques heures.`,
    };
  }
  if (!input.online || lastOutcome === 'offline') {
    return {
      level: 'attention',
      text: `Hors ligne : licence valable jusqu’au ${fmt(expSec)} (${String(daysLeft)} jour(s)). Le renouvellement sera tenté automatiquement au retour d’Internet ; sans cela, l’application passera en lecture seule le ${fmt(expSec)}. Vous pouvez aussi importer un fichier de licence.`,
    };
  }
  return {
    level: 'info',
    text: `Licence valable jusqu’au ${fmt(expSec)} (${String(daysLeft)} jour(s)). Le renouvellement automatique est en cours de fenêtre : si votre abonnement est renouvelé chez Nexxus, ce poste se met à jour tout seul.`,
  };
}
